// frontend/src/screens/admin/sps/PayRecordsBrowserScreen.jsx
import { useState } from 'react';
import {
  Container, Row, Col, Table, Button, Badge, Form,
  Modal, Alert, Spinner, Pagination, InputGroup,
} from 'react-bootstrap';
import {
  useGetPayRecordsQuery,
  useUpdatePayRecordMutation,
} from '../../../slices/spsApiSlices';

const YEAR_OPTIONS = Array.from({ length: 2025 - 2013 + 1 }, (_, i) => 2013 + i);

function EditModal({ record, onClose, onSaved }) {
  const [updateRecord, { isLoading }] = useUpdatePayRecordMutation();
  const [form, setForm] = useState({
    grossBasicPensionablePay:          record.grossBasicPensionablePay,
    grossBasicPensionableAllowances:   record.grossBasicPensionableAllowances,
    fteAtPayDate:                      record.fteAtPayDate,
    correctionNote:                    '',
  });
  const [error, setError] = useState('');

  const handleSave = async () => {
    try {
      await updateRecord({ id: record._id, ...form }).unwrap();
      onSaved();
    } catch (err) {
      setError(err?.data?.message || 'Save failed');
    }
  };

  const field = (label, key, step = '0.01') => (
    <Form.Group className="mb-3">
      <Form.Label className="small fw-semibold">{label}</Form.Label>
      <Form.Control
        type="number" step={step}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </Form.Group>
  );

  return (
    <Modal show centered onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: '1rem' }}>
          Manual Correction — {record.employeeName} ({record.ppsNumber})
          <div className="text-muted fw-normal" style={{ fontSize: '.82rem' }}>
            Pay period: {record.payPeriod} · {record.paymentFrequency}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {field('Gross Basic Pensionable Pay (€)', 'grossBasicPensionablePay')}
        {field('Gross Basic Pensionable Allowances (€)', 'grossBasicPensionableAllowances')}
        {field('FTE at Pay Date (0–1)', 'fteAtPayDate', '0.001')}
        <Form.Group className="mb-0">
          <Form.Label className="small fw-semibold">Reason for correction</Form.Label>
          <Form.Control
            as="textarea" rows={2}
            value={form.correctionNote}
            onChange={(e) => setForm({ ...form, correctionNote: e.target.value })}
            placeholder="Describe why this record is being corrected"
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="warning" onClick={handleSave} disabled={isLoading}>
          {isLoading ? <Spinner size="sm" animation="border" /> : 'Save Correction'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function PayRecordsBrowserScreen() {
  const [filters, setFilters] = useState({
    pps: '', year: '', freq: '', negativeOnly: false, manualOnly: false,
  });
  const [page,       setPage]       = useState(1);
  const [editRecord, setEditRecord] = useState(null);

  const queryParams = {
    page,
    limit: 50,
    ...(filters.pps          && { pps: filters.pps }),
    ...(filters.year         && { year: filters.year }),
    ...(filters.freq         && { freq: filters.freq }),
    ...(filters.negativeOnly && { negativeOnly: 'true' }),
    ...(filters.manualOnly   && { manualOnly: 'true' }),
  };

  const { data, isLoading, isFetching, isError, refetch } = useGetPayRecordsQuery(queryParams);
  const records = data?.records || [];
  const total   = data?.total   || 0;
  const pages   = data?.pages   || 1;

  const applyFilter = (updates) => {
    setFilters((f) => ({ ...f, ...updates }));
    setPage(1);
  };

  const totalPay = (r) => r.grossBasicPensionablePay + r.grossBasicPensionableAllowances;
  const isNeg    = (r) => totalPay(r) < 0;

  return (
    <Container fluid className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0">Pay Records</h4>
          <small className="text-muted">
            {total.toLocaleString()} records matching current filter
          </small>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="g-2 mb-3">
        <Col xs={12} sm={4} md={3}>
          <InputGroup size="sm">
            <InputGroup.Text>PPS</InputGroup.Text>
            <Form.Control
              placeholder="e.g. 1205726A"
              value={filters.pps}
              onChange={(e) => applyFilter({ pps: e.target.value.toUpperCase() })}
              style={{ fontFamily: 'monospace' }}
            />
          </InputGroup>
        </Col>
        <Col xs={6} sm={3} md={2}>
          <Form.Select size="sm" value={filters.year} onChange={(e) => applyFilter({ year: e.target.value })}>
            <option value="">All years</option>
            {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
          </Form.Select>
        </Col>
        <Col xs={6} sm={3} md={2}>
          <Form.Select size="sm" value={filters.freq} onChange={(e) => applyFilter({ freq: e.target.value })}>
            <option value="">All frequencies</option>
            <option value="Monthly">Monthly</option>
            <option value="Fortnightly">Fortnightly</option>
          </Form.Select>
        </Col>
        <Col xs="auto" className="d-flex gap-3 align-items-center">
          <Form.Check
            type="switch" id="negOnly" size="sm"
            label="Negative pay only"
            checked={filters.negativeOnly}
            onChange={(e) => applyFilter({ negativeOnly: e.target.checked })}
          />
          <Form.Check
            type="switch" id="manOnly" size="sm"
            label="Manual corrections only"
            checked={filters.manualOnly}
            onChange={(e) => applyFilter({ manualOnly: e.target.checked })}
          />
          <Button size="sm" variant="outline-secondary" onClick={() => { setFilters({ pps: '', year: '', freq: '', negativeOnly: false, manualOnly: false }); setPage(1); }}>
            Clear
          </Button>
        </Col>
      </Row>

      {isError && <Alert variant="danger">Failed to load pay records.</Alert>}

      <div className="table-responsive" style={{ position: 'relative' }}>
        {(isLoading || isFetching) && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,.6)', zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner animation="border" />
          </div>
        )}
        <Table bordered hover size="sm" className="mb-0" style={{ fontSize: '.8rem' }}>
          <thead className="table-dark">
            <tr>
              <th>PPS</th>
              <th>Name</th>
              <th>Period</th>
              <th>Year</th>
              <th>Freq</th>
              <th className="text-end">Gross Pay</th>
              <th className="text-end">Allowances</th>
              <th className="text-end">Total</th>
              <th className="text-end">FTE</th>
              <th>Flags</th>
              <th style={{ width: 60 }}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {records.length === 0 && !isLoading && (
              <tr><td colSpan={11} className="text-center text-muted py-3">No records found</td></tr>
            )}
            {records.map((r) => {
              const neg = isNeg(r);
              return (
                <tr key={r._id} style={{ backgroundColor: neg ? '#fff0f0' : r.isManualCorrection ? '#fffbe6' : undefined }}>
                  <td style={{ fontFamily: 'monospace' }}>{r.ppsNumber}</td>
                  <td>{r.employeeName}</td>
                  <td style={{ fontFamily: 'monospace' }}>{r.payPeriod}</td>
                  <td>{r.year}</td>
                  <td>
                    <Badge bg={r.paymentFrequency === 'Monthly' ? 'primary' : 'success'} style={{ fontSize: '.65rem' }}>
                      {r.paymentFrequency === 'Monthly' ? 'M' : 'FN'}
                    </Badge>
                  </td>
                  <td className="text-end" style={{ color: r.grossBasicPensionablePay < 0 ? '#dc3545' : undefined }}>
                    €{r.grossBasicPensionablePay.toFixed(2)}
                  </td>
                  <td className="text-end" style={{ color: r.grossBasicPensionableAllowances < 0 ? '#dc3545' : undefined }}>
                    €{r.grossBasicPensionableAllowances.toFixed(2)}
                  </td>
                  <td className="text-end fw-semibold" style={{ color: neg ? '#dc3545' : undefined }}>
                    €{totalPay(r).toFixed(2)}
                  </td>
                  <td className="text-end">{r.fteAtPayDate.toFixed(3)}</td>
                  <td>
                    {neg && <Badge bg="danger" className="me-1" style={{ fontSize: '.62rem' }}>NEG</Badge>}
                    {r.isManualCorrection && <Badge bg="warning" text="dark" style={{ fontSize: '.62rem' }}>EDITED</Badge>}
                  </td>
                  <td>
                    <Button size="sm" variant="outline-secondary" style={{ fontSize: '.72rem', padding: '1px 6px' }}
                      onClick={() => setEditRecord(r)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination size="sm">
            <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
            {Array.from({ length: Math.min(pages, 10) }, (_, i) => i + 1).map((p) => (
              <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
            ))}
            {pages > 10 && <Pagination.Ellipsis />}
            <Pagination.Next disabled={page === pages} onClick={() => setPage(page + 1)} />
          </Pagination>
        </div>
      )}

      {editRecord && (
        <EditModal
          record={editRecord}
          onClose={() => setEditRecord(null)}
          onSaved={() => { setEditRecord(null); refetch(); }}
        />
      )}
    </Container>
  );
}